/*
 * Wizard Step 4 — Preview (U8 — Mantine rebuild).
 *
 * Renders four observable phases with no blank screens:
 *
 *   A. Run created but no WS frame yet — "Starting preview…"
 *   B. PREPPING / UPLOADING — per-stage label + progress note
 *   C. AWAITING_CONFIRM reached, GET /runs/{id}/preview in flight — "Loading
 *      confirmation…" skeleton
 *   D. Ready — the confirmation table (one row per ad) built on Mantine `Table`
 *      + `Table.ScrollContainer`. Columns: creative thumbnail (hover-zoom),
 *      Ad (concept + ad-name sub-line, IMG/VID tokens bolded), Headline, CTA,
 *      Copy (truncated snippet with a full-copy hover popover). Failed tickets
 *      (`ok:false`) are marked distinctly. A summary line above the table states
 *      "M tickets · K ads ready · J will not launch".
 *
 * DISPLAY ONLY (U8). The interactive per-ad select column (U12) and the
 * Published/Draft launch wiring (U9) are intentionally NOT here — U12 keys its
 * select column on `AdRow.id` (the stable per-ad id surfaced below).
 *
 * Props:
 *   previewRunId    — the id of the preview run (null before it's created)
 *   runStatus       — the latest WS frame (`{ run_id, stage, status }`)
 *   connectionStatus — socket health ("connecting" | "open" | "closed")
 *   previewData     — the RunPreviewResponse (null until AWAITING_CONFIRM)
 *   createError     — POST /runs failure (surfaces Retry / Back-to-Settings)
 *   onRetryCreate   — fires a fresh POST /runs
 *   onBackToSettings — navigate back to step 3 (cancel + back)
 */

import { Fragment, type ReactNode } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  HoverCard,
  Image,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { errorMessage } from "../../api/errors";
import type { RunStatusFrame } from "../../ws/useRunStatus";
import type { RunPreviewResponse, PreviewTicketEntry } from "../../api/client";
import { heldTickets, namingGapLabel } from "./namingOverride";

/* ---- Stage labels -------------------------------------------------------- */

function stageLabel(stage: string, status: string): string {
  const s = stage.toLowerCase();
  const st = status.toLowerCase();
  if (st === "awaiting_confirm") return "Awaiting confirmation…";
  if (s === "prep" || s === "prepping" || st === "prepping" || st === "prepped")
    return "Preparing tickets…";
  if (
    s === "upload" ||
    s === "uploading" ||
    st === "uploading" ||
    st === "uploaded"
  )
    return "Uploading creatives…";
  if (s === "finalize" || s === "finalizing" || st === "finalizing")
    return "Finalizing…";
  // Generic fallback
  return `Processing… (${status})`;
}

/* ---- IMG/VID tokenizer (KTD-8) ------------------------------------------- */

/*
 * Presentational bolding of the AD_FORMAT marker in the ad-name sub-line.
 * Word-boundary, UPPERCASE-only: standalone `IMG` / `VID` whole-word tokens are
 * wrapped in <Text span fw={700}>. Lowercase / substring matches ("imgur",
 * "vidya") are untouched. Pure presentation — no data change.
 */
const FORMAT_TOKEN_RE = /\b(IMG|VID)\b/g;

function renderAdName(adName: string): ReactNode {
  const parts: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  // Fresh regex state per call (the literal carries `g`, so reset lastIndex).
  FORMAT_TOKEN_RE.lastIndex = 0;
  let i = 0;
  while ((match = FORMAT_TOKEN_RE.exec(adName)) !== null) {
    if (match.index > last) {
      parts.push(
        <Fragment key={`t${i}`}>{adName.slice(last, match.index)}</Fragment>,
      );
    }
    parts.push(
      <Text span fw={700} key={`b${i}`}>
        {match[0]}
      </Text>,
    );
    last = match.index + match[0].length;
    i += 1;
  }
  if (last < adName.length) {
    parts.push(<Fragment key={`t${i}`}>{adName.slice(last)}</Fragment>);
  }
  return parts;
}

/* ---- Copy truncation ----------------------------------------------------- */

const COPY_SNIPPET_LEN = 120;

function snippet(text: string): string {
  return text.length > COPY_SNIPPET_LEN
    ? text.slice(0, COPY_SNIPPET_LEN) + "…"
    : text;
}

/* ---- Ad row type for the Table ------------------------------------------ */

interface AdRow {
  /**
   * Stable per-ad id (`PreviewAdEntry.id`, added in U1). U12 keys its select
   * column on this. Failed-ticket placeholder rows carry a synthetic
   * `<ticket_id>-failed` key (no real ad) — they are never selectable.
   */
  id: string;
  ticketFailed: boolean;
  /**
   * 2026-10-06 — true when this ad sits on a ticket with blank master-sheet
   * naming cells that the operator has NOT overridden. A locked ad cannot be
   * selected and is excluded from an "all" launch (the server enforces both).
   */
  locked: boolean;
  /** The ticket's naming gap (set on every ad row of a held ticket). */
  namingGap: { count: number; cells: string[] } | null;
  concept: string;
  adName: string | null;
  variation: number;
  adsetLabel: string;
  /** 2026-10-19: where the ad set goes when the ticket named its own Notion
   *  campaign / landing page ("MD | CBO | New Angles · MD | GLP Adv"). */
  target: string | null;
  /** 2026-10-29: the optimization goal this ad set launches with, where it
   *  came from, and why it would be refused (null when unknown). */
  goal: string | null;
  goalSource: "campaign" | "setting" | null;
  goalWarning: string | null;
  pageId: string | null;
  link: string | null;
  headline: string | null;
  /**
   * U5 — true when PREP substituted the FB headline from the product's
   * `default_headline` (empty ticket Headline). Drives a "defaulted" badge.
   */
  headlineWasDefaulted: boolean;
  cta: string;
  /** Full copy (untruncated) — the snippet is derived at render time. */
  description: string;
  /**
   * U5 — true when PREP appended a plain destination link into the body copy
   * (no usable link present). Drives a "link inserted" badge.
   */
  bodyLinkWasInserted: boolean;
  /**
   * 2026-07-07 #2 — true when PREP auto-stripped script notation (stage
   * directions, Hook:/Body:/CTA: labels, speaker tags) from the body copy.
   * Drives a "script notation stripped" badge so the operator eyeballs the
   * cleaned copy before confirming.
   */
  scriptNotationStripped: boolean;
  mediaUrl: string | null;
  error: string | null;
}

/** Where a ticket's ad set goes when the ticket named its own campaign /
 *  landing page in Notion (2026-10-19), else null (the run's). */
function ticketTarget(t: { target_campaign_name?: string; landing_page_name?: string }): string | null {
  const parts = [t.target_campaign_name, t.landing_page_name].filter((x): x is string => !!x);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** "Conversions" / "Value" for Meta's goal names; anything else as sent. */
export function goalLabel(goal: string): string {
  if (goal === "OFFSITE_CONVERSIONS") return "Conversions";
  if (goal === "VALUE") return "Value";
  return goal;
}

function ticketGoal(t: PreviewTicketEntry): Pick<AdRow, "goal" | "goalSource" | "goalWarning"> {
  return {
    goal: t.optimization_goal ?? null,
    goalSource: t.goal_source ?? null,
    goalWarning: t.goal_warning ?? null,
  };
}

function buildAdRows(
  tickets: PreviewTicketEntry[],
  namingOverrides: ReadonlySet<string>,
): AdRow[] {
  const rows: AdRow[] = [];
  for (const t of tickets) {
    if (!t.ok) {
      // One placeholder row per failed ticket — no ad-level data.
      rows.push({
        id: `${t.ticket_id}-failed`,
        ticketFailed: true,
        locked: false,
        namingGap: null,
        concept: t.concept,
        adName: null,
        variation: 0,
        adsetLabel: t.adset_name ?? "—",
        target: ticketTarget(t),
        ...ticketGoal(t),
        pageId: t.page_id_used,
        link: t.link,
        headline: t.headline,
        headlineWasDefaulted: false,
        cta: "—",
        description: "—",
        bodyLinkWasInserted: false,
        scriptNotationStripped: false,
        mediaUrl: null,
        error: t.error ?? "Unknown error",
      });
      continue;
    }
    const gap = t.naming_gap ?? null;
    for (const ad of t.ads) {
      rows.push({
        id: ad.id,
        ticketFailed: false,
        locked: gap !== null && !namingOverrides.has(t.ticket_id),
        namingGap: gap,
        concept: t.concept,
        adName: ad.ad_name,
        variation: ad.variation,
        adsetLabel:
          ad.ad_sets.map((a) => a.label).join(", ") || t.adset_name || "—",
        target: ticketTarget(t),
        ...ticketGoal(t),
        pageId: ad.page ?? t.page_id_used,
        link: t.link,
        headline: ad.title || t.headline,
        headlineWasDefaulted: ad.headline_was_defaulted === true,
        cta: ad.cta,
        description: ad.description,
        bodyLinkWasInserted: ad.body_link_was_inserted === true,
        scriptNotationStripped: ad.script_notation_stripped === true,
        mediaUrl: ad.media_url,
        error: null,
      });
    }
  }
  return rows;
}

/* ---- Creative thumbnail with hover-zoom (R3 / KTD-7) -------------------- */

const THUMB_SIZE = 48;
const ZOOM_SIZE = 280;

// KTD-6 (plan 2026-06-20-002, U8): VID creatives are detected by the media-URL
// extension. The CDN object name is the content-hash stem `<stem>_<md5[:10]><ext>`
// (upload.ts), so a real CDN URL carries a `.mp4/.mov/.webm` extension — no
// `media_type` field is needed on the preview row.
const VIDEO_EXT_RE = /\.(mp4|mov|webm)(?:[?#]|$)/i;
function isVideoUrl(url: string): boolean {
  return VIDEO_EXT_RE.test(url);
}

function CreativeThumb({ src, alt }: { src: string; alt: string }) {
  // KTD-7: gate the tap fallback on `pointer: coarse` (touch), NOT a viewport
  // breakpoint. On touch devices HoverCard has no hover story, so a tap opens a
  // Modal instead.
  const isCoarse = useMediaQuery("(pointer: coarse)");
  const [opened, modal] = useDisclosure(false);

  const isVideo = isVideoUrl(src);
  const videoLabel = alt || "Video creative";

  // U8: render a <video> poster (first frame) for VID creatives instead of a
  // broken <img>. Muted + no autoplay; `preload="metadata"` surfaces the first
  // frame as the thumbnail. The zoom variant adds controls for click-to-play.
  const thumb = isVideo ? (
    <video
      src={src}
      aria-label={videoLabel}
      muted
      preload="metadata"
      width={THUMB_SIZE}
      height={THUMB_SIZE}
      style={{
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        objectFit: "cover",
        borderRadius: "var(--mantine-radius-sm)",
        display: "block",
      }}
    />
  ) : (
    <Image
      src={src}
      alt={alt}
      w={THUMB_SIZE}
      h={THUMB_SIZE}
      radius="sm"
      fit="cover"
    />
  );

  const zoom = isVideo ? (
    <video
      src={src}
      aria-label={videoLabel}
      muted
      controls
      preload="metadata"
      style={{
        width: ZOOM_SIZE,
        maxWidth: "100%",
        borderRadius: "var(--mantine-radius-sm)",
        display: "block",
      }}
    />
  ) : (
    <Image src={src} alt={alt} w={ZOOM_SIZE} fit="contain" radius="sm" />
  );

  if (isCoarse) {
    return (
      <>
        <Box
          component="button"
          type="button"
          onClick={modal.open}
          aria-label="Preview creative"
          bg="transparent"
          p={0}
          style={{ border: "none", cursor: "zoom-in", lineHeight: 0 }}
        >
          {thumb}
        </Box>
        <Modal
          opened={opened}
          onClose={modal.close}
          withCloseButton
          centered
          size="auto"
          title="Creative preview"
        >
          {zoom}
        </Modal>
      </>
    );
  }

  return (
    <HoverCard width={ZOOM_SIZE + 24} shadow="md" position="right" withinPortal>
      <HoverCard.Target>
        <Box style={{ cursor: "zoom-in", lineHeight: 0 }}>{thumb}</Box>
      </HoverCard.Target>
      <HoverCard.Dropdown p="xs">{zoom}</HoverCard.Dropdown>
    </HoverCard>
  );
}

function NoThumb() {
  // Rows without media render a non-interactive placeholder (KTD-7): no
  // HoverCard, no tap target — avoids an invisible tap target beside real ones.
  return (
    <Box
      aria-hidden="true"
      w={THUMB_SIZE}
      h={THUMB_SIZE}
      style={{
        borderRadius: "var(--mantine-radius-sm)",
        border: "1px solid var(--mantine-color-gray-3)",
        background: "var(--mantine-color-gray-1)",
      }}
    />
  );
}

/* ---- Copy cell with full-copy hover (R4) -------------------------------- */

function CopyCell({ text }: { text: string }) {
  const truncated = text.length > COPY_SNIPPET_LEN;
  const snip = snippet(text);
  if (!truncated) {
    return (
      <Text fz="xs" c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
        {text}
      </Text>
    );
  }
  return (
    <HoverCard width={320} shadow="md" position="top" withinPortal>
      <HoverCard.Target>
        <Text
          fz="xs"
          c="dimmed"
          style={{ whiteSpace: "pre-wrap", cursor: "help" }}
        >
          {snip}
        </Text>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <ScrollArea.Autosize mah={240} type="auto">
          <Text fz="xs" style={{ whiteSpace: "pre-wrap" }}>
            {text}
          </Text>
        </ScrollArea.Autosize>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}

/* ---- Table row ----------------------------------------------------------- */

function AdTableRow({
  row,
  selected,
  onToggle,
  selectDisabled,
}: {
  row: AdRow;
  selected: boolean;
  onToggle: (checked: boolean) => void;
  selectDisabled: boolean;
}) {
  return (
    <Table.Tr>
      <Table.Td>
        {row.ticketFailed ? (
          // Failed tickets are non-selectable — a VISIBLE disabled checkbox
          // (not absent) so the column stays legible (U12).
          <Checkbox disabled aria-label="Cannot select: ticket failed" />
        ) : row.locked ? (
          // Held for blank naming cells — visible + disabled until the ticket
          // is overridden ("Launch anyway") in the notice above the table.
          <Checkbox
            disabled
            aria-label={`Cannot select ${row.concept}: blank naming cells, tick Launch anyway first`}
          />
        ) : (
          <Checkbox
            checked={selected}
            disabled={selectDisabled}
            onChange={(e) => onToggle(e.currentTarget.checked)}
            aria-label={`Select ${row.concept}${row.adName ? ` — ${row.adName}` : ""}`}
          />
        )}
      </Table.Td>
      <Table.Td>
        {row.mediaUrl ? (
          <CreativeThumb src={row.mediaUrl} alt="" />
        ) : (
          <NoThumb />
        )}
      </Table.Td>
      <Table.Td>
        <Text
          component="span"
          td={row.ticketFailed ? "line-through" : undefined}
          c={row.ticketFailed ? "dimmed" : undefined}
        >
          {row.concept}
        </Text>
        {row.adName ? (
          <Text fz="xs" c="dimmed" mt={2} display="block">
            {renderAdName(row.adName)}
          </Text>
        ) : null}
        {row.namingGap ? (
          <Badge
            size="xs"
            mt={4}
            color={row.locked ? "yellow" : "orange"}
            variant="light"
            title={namingGapLabel(row.namingGap)}
          >
            {row.locked ? "held: blank naming cell" : "override: launching anyway"}
          </Badge>
        ) : null}
      </Table.Td>
      <Table.Td>
        {/* U5 — the ad set each ad uploads into (per-ad ad_sets, falling back to
            the ticket adset_name). buildAdRows already resolved this to adsetLabel. */}
        {row.adsetLabel === "—" ? (
          <Text component="span" c="dimmed">
            —
          </Text>
        ) : (
          row.adsetLabel
        )}
        {row.target && (
          <Text fz="xs" c="dimmed" mt={2}>
            → {row.target}
          </Text>
        )}
        {row.goal && (
          <Badge
            size="xs"
            mt={4}
            variant="light"
            color={row.goal === "VALUE" ? "grape" : "blue"}
            title={
              row.goalSource === "campaign"
                ? "Taken from the campaign's own ad sets"
                : "From the launch's goal setting (the campaign has no ad sets to read it from)"
            }
          >
            Goal: {goalLabel(row.goal)}
          </Badge>
        )}
        {row.goalWarning && (
          <Text fz="xs" c="red" mt={2}>
            {row.goalWarning}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        {/* U5 — badge (headline auto-defaulted from the product default) sits
            inline with the headline value so the operator can eyeball the PREP
            substitution before confirming. Read-only display. */}
        <Group gap="xs" wrap="nowrap" align="center">
          {row.headline ? (
            <Text component="span">{row.headline}</Text>
          ) : (
            <Text component="span" c="dimmed">
              —
            </Text>
          )}
          {row.headlineWasDefaulted ? (
            <Badge size="xs" color="gray" variant="light">
              defaulted
            </Badge>
          ) : null}
        </Group>
      </Table.Td>
      <Table.Td>{row.cta}</Table.Td>
      <Table.Td>
        {row.ticketFailed ? (
          <Text component="span" role="note" aria-label="Error" fz="xs" c="red">
            ✗ {row.error}
          </Text>
        ) : (
          <Stack gap={4} align="flex-start">
            <CopyCell text={row.description} />
            {/* U5 — badge (body link auto-inserted by PREP) next to the copy so
                the operator sees the injected link before confirming. */}
            {row.bodyLinkWasInserted ? (
              <Badge size="xs" color="gray" variant="light">
                link inserted
              </Badge>
            ) : null}
            {/* 2026-07-07 #2 — script notation was auto-stripped from this
                copy; the operator should eyeball it before confirming. */}
            {row.scriptNotationStripped ? (
              <Badge size="xs" color="yellow" variant="light">
                script notation stripped
              </Badge>
            ) : null}
          </Stack>
        )}
      </Table.Td>
    </Table.Tr>
  );
}

/* ---- Terminal statuses that mean the run is done and failed -------------- */

// The backend emits `aborted` (never `cancelled`); `drafted` is a SUCCESS
// terminal, not a failure, so it is deliberately absent here.
const TERMINAL_FAILURE_STATUSES = new Set(["failed", "aborted"]);

function isTerminalFailure(status: string): boolean {
  return TERMINAL_FAILURE_STATUSES.has(status.toLowerCase());
}

/* ---- Sub-components ------------------------------------------------------ */

interface PhaseFailedProps {
  stage: string;
  previewRunId: string;
  onBackToSettings: () => void;
}

function PhaseFailed({ stage, previewRunId, onBackToSettings }: PhaseFailedProps) {
  const stageDisplay = stage.charAt(0).toUpperCase() + stage.slice(1).toLowerCase();
  return (
    <Alert color="red" role="alert" title="Preview failed">
      <Stack gap="xs">
        <Text fz="sm">
          The run failed during the <strong>{stageDisplay}</strong> stage.
        </Text>
        <Text fz="sm" c="dimmed">
          No Notion tickets were stamped and nothing was pushed to Meta, so it is safe
          to start over. Check the run detail page for the full error message, then go
          back to Settings to fix the issue and try again.
        </Text>
        <Group gap="md" mt={4}>
          <Anchor href={`/runs/${previewRunId}`} fz="sm" fw={500}>
            See what failed →
          </Anchor>
          <Button variant="default" size="xs" onClick={onBackToSettings}>
            Back to Settings
          </Button>
        </Group>
      </Stack>
    </Alert>
  );
}

function PhaseSpinner({ label }: { label: string }) {
  return (
    <Group gap="sm" py="xl" px="md" role="status" aria-live="polite">
      <Loader size="sm" aria-hidden="true" />
      <Text fz="sm" c="dimmed">
        {label}
      </Text>
    </Group>
  );
}

interface PhaseDProps {
  data: RunPreviewResponse;
  selectedAdIds: Set<string>;
  onToggleAd: (id: string, checked: boolean) => void;
  onToggleAllAds: (checked: boolean) => void;
  draftMode: boolean;
  namingOverrides: ReadonlySet<string>;
  onToggleNamingOverride: (ticketId: string, checked: boolean) => void;
}

function PhaseD({
  data,
  selectedAdIds,
  onToggleAd,
  onToggleAllAds,
  draftMode,
  namingOverrides,
  onToggleNamingOverride,
}: PhaseDProps) {
  const tickets = data.tickets;
  const totalTickets = tickets.length;
  const failedTickets = tickets.filter((t) => !t.ok).length;
  const readyTickets = totalTickets - failedTickets;

  const rows = buildAdRows(tickets, namingOverrides);
  // Selectable = the ready (non-failed), non-held ad rows; the select column
  // keys on these. Held ads (blank naming cells, not overridden) are excluded
  // from every count below so "All N ads will launch" never includes them.
  const selectableIds = rows.filter((r) => !r.ticketFailed && !r.locked).map((r) => r.id);
  const held = heldTickets(tickets);
  const heldAdCount = rows.filter((r) => r.locked).length;
  const totalAds = selectableIds.length;
  const selectedCount = selectableIds.filter((id) => selectedAdIds.has(id)).length;
  const allChecked = totalAds > 0 && selectedCount === totalAds;
  const someChecked = selectedCount > 0 && selectedCount < totalAds;

  // Make the empty⇒all inversion EXPLICIT — this is the last screen before an
  // irreversible Meta write (U12) or an irreversible Notion stamp (Draft). Draft now
  // honors the per-ad selection (U7): it is seeded to all on entry, so 0 selected is
  // a deliberate "draft nothing" that the submit blocks (U8), never a silent "all".
  const selectionSummary =
    selectedCount === 0
      ? draftMode
        ? "No ads selected to draft"
        : `All ${totalAds} ad${totalAds !== 1 ? "s" : ""} will launch`
      : `${selectedCount} of ${totalAds} ad${totalAds !== 1 ? "s" : ""} selected`;

  return (
    <Stack gap="sm">
      <Group justify="space-between" wrap="nowrap" align="baseline">
        <Text fz="sm" c="dimmed" role="status">
          {totalTickets} ticket{totalTickets !== 1 ? "s" : ""} · {totalAds} ad
          {totalAds !== 1 ? "s" : ""} ready
          {failedTickets > 0 ? ` · ${failedTickets} will not launch` : ""}
          {heldAdCount > 0
            ? ` · ${heldAdCount} ad${heldAdCount !== 1 ? "s" : ""} held for blank naming cells`
            : ""}
          {readyTickets === 0 && failedTickets > 0 ? " — all tickets failed" : ""}
        </Text>
        <Text fz="sm" fw={500} role="status">
          {selectionSummary}
        </Text>
      </Group>

      {held.length > 0 ? (
        <Alert color="yellow" variant="light" title="Blank cells in the master sheet ad name">
          <Stack gap="xs">
            <Text fz="sm">
              These tickets have blank naming cells, so their ad names contain a gap. They
              will not launch unless you tick <strong>Launch anyway</strong>. The name
              launches exactly as the sheet built it.
            </Text>
            {held.map((t) => (
              <Checkbox
                key={t.ticket_id}
                checked={namingOverrides.has(t.ticket_id)}
                onChange={(e) => onToggleNamingOverride(t.ticket_id, e.currentTarget.checked)}
                label={
                  <Text fz="sm" component="span">
                    <strong>Launch anyway:</strong> {t.concept} —{" "}
                    {t.naming_gap ? namingGapLabel(t.naming_gap) : ""}
                  </Text>
                }
              />
            ))}
          </Stack>
        </Alert>
      ) : null}

      <Table.ScrollContainer minWidth={720}>
        <Table highlightOnHover verticalSpacing="xs" striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={40}>
                <Checkbox
                  aria-label="Select all ads"
                  checked={allChecked}
                  indeterminate={someChecked}
                  // U7: per-ad selection is enabled in BOTH modes now.
                  disabled={totalAds === 0}
                  onChange={(e) => onToggleAllAds(e.currentTarget.checked)}
                />
              </Table.Th>
              <Table.Th w={64} aria-label="Creative" />
              <Table.Th>Ad</Table.Th>
              <Table.Th>Ad set</Table.Th>
              <Table.Th>Headline</Table.Th>
              <Table.Th w={140}>CTA</Table.Th>
              <Table.Th>Copy</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text c="dimmed" fz="sm">
                    No ads to preview.
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              rows.map((row) => (
                <AdTableRow
                  key={row.id}
                  row={row}
                  selected={selectedAdIds.has(row.id)}
                  onToggle={(checked) => onToggleAd(row.id, checked)}
                  // U7: ready ads are selectable in both Published and Draft.
                  selectDisabled={false}
                />
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Stack>
  );
}

/* ---- Main component ------------------------------------------------------ */

export interface Step4PreviewProps {
  previewRunId: string | null;
  runStatus: RunStatusFrame | null;
  connectionStatus: string;
  previewData: RunPreviewResponse | null | undefined;
  isPreviewLoading: boolean;
  /** 2026-11-02: the preview fetch failed. Shown with Retry instead of the
   *  endless "Awaiting confirmation…" spinner the OO runs sat on. */
  previewError?: unknown;
  onRetryPreview?: () => void;
  createError: unknown | null;
  onRetryCreate: () => void;
  onBackToSettings: () => void;
  /** Per-ad selection (U12) — lifted into LaunchWizard; empty ⇒ launch all. */
  selectedAdIds: Set<string>;
  onToggleAd: (id: string, checked: boolean) => void;
  onToggleAllAds: (checked: boolean) => void;
  /** When Draft (U9) the select column is disabled — all ads are staged. */
  draftMode: boolean;
  /**
   * 2026-10-06 — tickets with blank naming cells the operator chose to launch
   * anyway. Optional so callers without the feature keep working (nothing
   * overridden ⇒ held tickets stay locked).
   */
  namingOverrides?: ReadonlySet<string>;
  onToggleNamingOverride?: (ticketId: string, checked: boolean) => void;
}

const NO_OVERRIDES: ReadonlySet<string> = new Set();

export function Step4Preview({
  previewRunId,
  runStatus,
  connectionStatus,
  previewData,
  isPreviewLoading,
  previewError = null,
  onRetryPreview = () => {},
  createError,
  onRetryCreate,
  onBackToSettings,
  selectedAdIds,
  onToggleAd,
  onToggleAllAds,
  draftMode,
  namingOverrides = NO_OVERRIDES,
  onToggleNamingOverride = () => {},
}: Step4PreviewProps): ReactNode {
  // POST /runs failed — surface inline with Retry / Back-to-Settings.
  if (createError !== null && previewRunId === null) {
    const msg = errorMessage(createError);
    return (
      <Stack gap="sm">
        <Alert color="red">Could not start the preview run: {msg}</Alert>
        <Group gap="sm">
          <Button variant="default" size="xs" onClick={onRetryCreate}>
            Retry
          </Button>
          <Button variant="default" size="xs" onClick={onBackToSettings}>
            Back to Settings
          </Button>
        </Group>
      </Stack>
    );
  }

  // Phase A — run created, no WS frame yet (or no run id yet).
  if (!previewRunId || !runStatus) {
    return <PhaseSpinner label="Starting preview…" />;
  }

  const status = runStatus.status.toLowerCase();

  // Phase D — table ready.
  if (status === "awaiting_confirm" && previewData && !isPreviewLoading) {
    return (
      <PhaseD
        data={previewData}
        selectedAdIds={selectedAdIds}
        onToggleAd={onToggleAd}
        onToggleAllAds={onToggleAllAds}
        draftMode={draftMode}
        namingOverrides={namingOverrides}
        onToggleNamingOverride={onToggleNamingOverride}
      />
    );
  }

  // Phase C — AWAITING_CONFIRM reached but preview data still loading.
  if (status === "awaiting_confirm" && isPreviewLoading) {
    return <PhaseSpinner label="Loading confirmation…" />;
  }

  // The preview fetch failed (2026-11-02): say so, with Retry, rather than
  // falling through to the "Awaiting confirmation…" spinner with no Launch.
  if (status === "awaiting_confirm" && previewError) {
    return (
      <Stack gap="sm">
        <Alert color="red">The preview could not be loaded: {errorMessage(previewError)}</Alert>
        <Group gap="sm">
          <Button variant="default" size="xs" onClick={onRetryPreview}>
            Retry
          </Button>
          <Button variant="default" size="xs" onClick={onBackToSettings}>
            Back to Settings
          </Button>
        </Group>
      </Stack>
    );
  }

  // Terminal failure — the run died in FAILED / ABORTED / CANCELLED.
  // Must check before Phase B so we never show "Processing…" for a dead run.
  if (isTerminalFailure(status)) {
    return (
      <PhaseFailed
        stage={runStatus.stage}
        previewRunId={previewRunId}
        onBackToSettings={onBackToSettings}
      />
    );
  }

  // Phase B — prep/upload in flight.
  const label = stageLabel(runStatus.stage, runStatus.status);

  // Reconnecting banner overlaid on phase B when WS lost.
  const reconnecting =
    connectionStatus === "closed" || connectionStatus === "connecting";

  return (
    <Stack gap="xs">
      {reconnecting && (
        <Text fz="xs" c="dimmed" role="status" aria-live="polite">
          Reconnecting to status stream…
        </Text>
      )}
      <PhaseSpinner label={label} />
    </Stack>
  );
}
